import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Link2, Package, Search, Upload } from 'lucide-react';
import api from '@/lib/api';
import { usePode } from '@/stores/authStore';
import Preloader from '@/components/Preloader';
import { CABECALHO, CELULA, COLUNA_PRODUTO, LINHA, TITULO_COLUNA } from '../cotacoes/tabela';
import { NOME_FAMILIA, type ListaDeProdutos } from './tipos';

const numero = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

/** O que a lista empresta ao produto aberto por cima dela. */
export type ContextoDaLista = { comInativos: boolean };

/**
 * O cadastro de produtos. A linha é o cabo que vai para o fornecedor — sem cor
 * e sem marca —, e é nele que a equalização compara preço; os códigos do ERP,
 * um por cor e por marca, aparecem dentro de cada um.
 */
export default function ProdutosPage() {
  const podeCadastrar = usePode('cadastro.produtos');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [marca, setMarca] = useState('');
  const [comInativos, setComInativos] = useState(false);

  // O comprador digita um código de cinco dígitos; pedir a cada tecla são cinco
  // idas ao banco remoto para uma resposta só.
  useEffect(() => {
    const espera = setTimeout(() => setBuscaAplicada(busca.trim()), 300);

    return () => clearTimeout(espera);
  }, [busca]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['produtos', buscaAplicada, marca, comInativos],
    queryFn: async () =>
      (
        await api.get<ListaDeProdutos>('/produtos', {
          params: {
            busca: buscaAplicada || undefined,
            marca: marca || undefined,
            inativos: comInativos ? 1 : undefined,
          },
        })
      ).data,
  });

  return (
    <div className="px-lg py-lg">
      <div className="mb-lg flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-ink">Produtos</h1>
          <p className="text-[13px] text-muted">
            Os códigos da Condusvale reunidos no cabo que vai para a cotação, e como cada fornecedor o chama.
          </p>
        </div>

        {podeCadastrar && (
          <Link
            to="/produtos/importar"
            className="inline-flex shrink-0 items-center gap-xs rounded-md border border-accent bg-accent/10 px-md py-sm text-[13px] font-bold text-accent hover:bg-accent hover:text-white"
          >
            <Upload size={15} aria-hidden="true" />
            Importar tabela de itens
          </Link>
        )}
      </div>

      <div className="mb-md flex flex-wrap items-center gap-md">
        <label className="relative min-w-[260px] flex-1">
          <Search size={15} className="absolute left-sm top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
          <input
            type="search"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Produto, descrição do ERP ou código"
            className="w-full rounded-md border border-border bg-surface py-sm pl-[32px] pr-sm text-[13px] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          />
        </label>

        <select
          value={marca}
          onChange={(evento) => setMarca(evento.target.value)}
          className="rounded-md border border-border bg-surface px-sm py-sm text-[13px] text-ink focus:border-accent focus:outline-none"
        >
          <option value="">Todas as marcas</option>
          {(data?.meta.marcas ?? []).map((nome) => (
            <option key={nome} value={nome}>
              {nome}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-xs text-[13px] text-ink-soft">
          <input
            type="checkbox"
            checked={comInativos}
            onChange={(evento) => setComInativos(evento.target.checked)}
            className="size-[15px] accent-[var(--color-accent)]"
          />
          Mostrar fora de linha
        </label>
      </div>

      {!!data?.meta.sem_produto && (
        <p className="mb-md flex items-start gap-sm rounded-md border border-warning/30 bg-warning/5 px-md py-sm text-[13px] text-ink-soft">
          <AlertTriangle size={15} className="mt-[2px] shrink-0 text-warning" aria-hidden="true" />
          {data.meta.sem_produto === 1
            ? '1 código do ERP não virou produto de cotação — é cabo de alarme, de rede ou coisa parecida, que o comprador não cota por bitola.'
            : `${data.meta.sem_produto} códigos do ERP não viraram produto de cotação — são cabos de alarme, de rede e parecidos, que o comprador não cota por bitola.`}
        </p>
      )}

      {isLoading && <Preloader telaCheia mensagem="Carregando produtos…" className="py-3xl" />}

      {isError && (
        <p className="text-[14px] text-danger">
          Não foi possível carregar os produtos. Recarregue a página para tentar de novo.
        </p>
      )}

      {data && data.data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface px-lg py-3xl text-center">
          <Package size={28} className="mx-auto mb-sm text-muted" aria-hidden="true" />
          <p className="text-[14px] font-bold text-ink">
            {buscaAplicada || marca ? 'Nenhum produto com esse filtro.' : 'O cadastro ainda está vazio.'}
          </p>
          <p className="text-[13px] text-muted">
            {buscaAplicada || marca
              ? 'Tente outro termo, ou marque "mostrar fora de linha".'
              : 'Importe a tabela de itens do Compumate para começar.'}
          </p>
        </div>
      )}

      {data && data.data.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-surface">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} ${COLUNA_PRODUTO} text-left`}>Produto</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Família</th>
                  <th className={`${TITULO_COLUNA} whitespace-nowrap text-right`}>Seção</th>
                  <th className={`${TITULO_COLUNA} whitespace-nowrap text-right`}>Vias</th>
                  <th className={`${TITULO_COLUNA} whitespace-nowrap text-right`}>Códigos</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Marcas</th>
                  <th className={`${TITULO_COLUNA} text-left`}>De-para</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((produto) => (
                  <tr key={produto.id} className={LINHA}>
                    <td className={`${CELULA} ${COLUNA_PRODUTO}`}>
                      <Link to={`/produtos/${produto.id}`} className="font-bold text-ink hover:text-accent">
                        {produto.descricao}
                      </Link>
                    </td>
                    <td className={CELULA}>{NOME_FAMILIA[produto.familia] ?? produto.familia}</td>
                    <td className={`${CELULA} text-right tabular-nums`}>
                      {produto.secao_mm2 ? `${numero.format(produto.secao_mm2)} mm²` : '—'}
                    </td>
                    <td className={`${CELULA} text-right tabular-nums`}>{produto.vias}</td>
                    <td className={`${CELULA} text-right tabular-nums`}>
                      {produto.itens_erp.total}
                      {produto.itens_erp.ativos < produto.itens_erp.total && (
                        <span className="text-muted"> ({produto.itens_erp.ativos} em linha)</span>
                      )}
                    </td>
                    <td className={CELULA}>
                      {produto.itens_erp.marcas.length ? produto.itens_erp.marcas.join(', ') : '—'}
                    </td>
                    <td className={CELULA}>
                      {produto.vinculos.total === 0 ? (
                        <span className="text-muted">Nenhum fornecedor ainda</span>
                      ) : (
                        <span className="inline-flex items-center gap-xs text-ink-soft">
                          <Link2 size={13} className="shrink-0 text-muted" aria-hidden="true" />
                          {produto.vinculos.fornecedores.join(', ')}
                          <span className="text-muted">
                            ({produto.vinculos.confirmados} de {produto.vinculos.total} conferidos)
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-sm text-[12px] text-muted">
            {data.meta.total === 1 ? '1 produto' : `${data.meta.total} produtos`}
          </p>
        </>
      )}

      {/* O produto escolhido abre aqui, por cima: a lista continua montada, com
          a busca, o filtro e a posição de rolagem onde estavam. O filtro de
          fora de linha vai junto — o que está escondido na lista não pode
          reaparecer dentro do produto. */}
      <Outlet context={{ comInativos } satisfies ContextoDaLista} />
    </div>
  );
}
